import React, { useEffect, useState, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import './App.css';
import { useAuth } from './hooks/useAuth';
import { AuthProvider } from './context/AuthContext';

// Pages
import HomePage from './pages/HomePage';
import HomeLandingPage from './pages/home/HomeLandingPage';
import ToolsPage from './pages/tools/ToolsPage';
import CourseDetailPage from './pages/CourseDetailPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import DashboardPage from './pages/DashboardPage';
import CommunityPage from './pages/CommunityPage';
import ClassesPage from './pages/ClassesPage';
import CalendarPage from './pages/CalendarPage';
import MembersPage from './pages/MembersPage';
import LeaderboardPage from './pages/LeaderboardPage';
import AboutPage from './pages/AboutPage';
import ProfilePage from './pages/ProfilePage';
import UserProfilePage from './pages/UserProfilePage';
import AdminPage from './pages/AdminPage';
import AdminLayout from './pages/admin/AdminLayout';
import AdminUsersPage from './pages/admin/AdminUsersPage';
import AdminUploadVideoPage from './pages/admin/AdminUploadVideoPage';
import AdminEnrollmentsPage from './pages/admin/AdminEnrollmentsPage';
import AdminCategoriesPage from './pages/admin/AdminCategoriesPage';
import AdminCoursesPage from './pages/admin/AdminCoursesPage';
import AdminPostsPage from './pages/admin/AdminPostsPage';
import AdminPostCategoriesPage from './pages/admin/AdminPostCategoriesPage';
import AdminTasksPage from './pages/admin/AdminTasksPage';
import AdminCourseTasksPage from './pages/admin/AdminCourseTasksPage';
import AdminCourseDetailPage from './pages/admin/AdminCourseDetailPage';
import AdminLevelsPage from './pages/admin/AdminLevelsPage';
import AdminSchoolSlidesPage from './pages/admin/AdminSchoolSlidesPage';
import AdminYouTubeNotificationsPage from './pages/admin/AdminYouTubeNotificationsPage';
import AdminFreeKeysPage from './pages/admin/AdminFreeKeysPage';
import AdminTranscriptTokensPage from './pages/admin/AdminTranscriptTokensPage';
import AdminEventsPage from './pages/admin/AdminEventsPage';
import AdminFakeUsersPage from './pages/admin/AdminFakeUsersPage';
import AdminYouTubeViewPage from './pages/admin/AdminYouTubeViewPage';
import TranscriptPage from './pages/TranscriptPage';
import CheckViewPage from './pages/CheckViewPage';
import FreeKeysPage from './pages/FreeKeysPage';
import TasksPage from './pages/TasksPage';
import SettingsPage from './pages/SettingsPage';
import NotificationsPage from './pages/NotificationsPage';
import ChatPage from './pages/ChatPage';
import SupportChatPage from './pages/SupportChatPage';
import PaymentPage from './pages/PaymentPage';
import EventPage from './pages/EventPage';

// Components
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import { ToastProvider } from './components/Toast';
import YouTubeNotificationBanner from './components/YouTubeNotificationBanner';

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/about" replace />;
  }

  return <>{children}</>;
};

// Admin Protected Route Component
const AdminProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/about" replace />;
  }

  if (user.role !== 'admin') {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

// Public Only Route - Redirect logged-in users away from login/register
const PublicOnlyRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  // If logged in, redirect to tools page
  if (user) {
    return <Navigate to="/tools" replace />;
  }

  return <>{children}</>;
};

function AppContent() {
  const { user, loading } = useAuth();
  const isAuthenticated = !!user;
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');
  const isCourseRoute = location.pathname.startsWith('/courses');
  const isLandingRoute = location.pathname === '/';
  const videoRef = useRef<HTMLVideoElement | null>(null);

  // Show Navbar: always on landing (marketing header), or when authenticated on other pages
  const showNavbar = isLandingRoute || isAuthenticated;

  // Áp dụng nền pastel gradient cho body
  useEffect(() => {
    const body = document.body;
    if (isAdminRoute) {
      body.style.backgroundColor = '#f7f7f7';
      body.style.backgroundImage = 'none';
    } else if (isCourseRoute) {
      body.style.backgroundColor = '#fff';
      body.style.backgroundImage = 'none';
    } else {
      // Premium pastel gradient
      body.style.background = 'linear-gradient(135deg, #F7F9FF 0%, #FBF7FF 50%, #F7FFFC 100%)';
      body.style.backgroundAttachment = 'fixed';
    }
    body.style.minHeight = '100vh';
  }, [isAdminRoute, isCourseRoute]);

  // Gắn class cho body khi ở trang course để tùy biến layout (dính header/footer)
  useEffect(() => {
    const body = document.body;
    if (isCourseRoute) {
      body.classList.add('course-page');
    } else {
      body.classList.remove('course-page');
    }
    return () => {
      body.classList.remove('course-page');
    };
  }, [isCourseRoute]);

  // Safely control background video playback to avoid play/pause race warnings
  useEffect(() => {
    const videoEl = videoRef.current;
    if (!videoEl || isAdminRoute) return;

    const playPromise = videoEl.play();
    if (playPromise && typeof playPromise.then === 'function') {
      playPromise.catch(() => {
        // Ignore autoplay rejection or interruption warnings
      });
    }

    return () => {
      try {
        videoEl.pause();
      } catch {
        /* ignore */
      }
    };
  }, [isAdminRoute]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <ToastProvider>
      <div className="App">
        {/* Premium blur blobs for non-admin routes */}
        {!isAdminRoute && (
          <>
            <style>{`
              .blob {
                position: fixed;
                border-radius: 50%;
                filter: blur(80px);
                opacity: 0.4;
                pointer-events: none;
                z-index: 0;
                animation: float 20s ease-in-out infinite;
              }

              .blob-1 {
                top: -10%;
                right: -5%;
                width: 500px;
                height: 500px;
                background: linear-gradient(135deg, rgba(109, 140, 255, 0.3) 0%, rgba(167, 139, 250, 0.2) 100%);
                animation-delay: 0s;
              }

              .blob-2 {
                bottom: -10%;
                left: -5%;
                width: 450px;
                height: 450px;
                background: linear-gradient(135deg, rgba(167, 139, 250, 0.25) 0%, rgba(103, 232, 249, 0.15) 100%);
                animation-delay: 7s;
              }

              .blob-3 {
                top: 40%;
                left: 40%;
                width: 350px;
                height: 350px;
                background: radial-gradient(circle, rgba(103, 232, 249, 0.2) 0%, transparent 70%);
                animation-delay: 14s;
              }

              @keyframes float {
                0%, 100% {
                  transform: translate(0, 0) scale(1);
                }
                33% {
                  transform: translate(30px, -30px) scale(1.1);
                }
                66% {
                  transform: translate(-20px, 20px) scale(0.9);
                }
              }
            `}</style>

            <div className="blob blob-1" aria-hidden="true" />
            <div className="blob blob-2" aria-hidden="true" />
            <div className="blob blob-3" aria-hidden="true" />
          </>
        )}
        <YouTubeNotificationBanner />
        {showNavbar && <Navbar />}
        <main className={`main-content ${isCourseRoute ? 'main-content--course' : ''}`}>
          <Routes>
            {/* Auth pages - redirect logged-in users to /tools */}
            <Route path="/login" element={<PublicOnlyRoute><LoginPage /></PublicOnlyRoute>} />
            <Route path="/register" element={<PublicOnlyRoute><RegisterPage /></PublicOnlyRoute>} />
            <Route path="/forgot-password" element={<PublicOnlyRoute><ForgotPasswordPage /></PublicOnlyRoute>} />
            <Route path="/reset-password" element={<PublicOnlyRoute><ResetPasswordPage /></PublicOnlyRoute>} />

            {/* Trang About - luôn có thể truy cập */}
            <Route path="/about" element={<AboutPage />} />

            {/* NEW Landing page at root - accessible to all */}
            <Route path="/" element={<HomeLandingPage />} />

            {/* Tools page - old homepage content */}
            <Route
              path="/tools"
              element={isAuthenticated ? <ToolsPage /> : <Navigate to="/about" replace />}
            />

            {/* Các trang cần đăng nhập */}
            <Route
              path="/courses/:id"
              element={
                <ProtectedRoute>
                  <CourseDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Navigate to="/community" replace />
                </ProtectedRoute>
              }
            />
            <Route
              path="/community"
              element={
                <ProtectedRoute>
                  <CommunityPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/notifications"
              element={
                <ProtectedRoute>
                  <NotificationsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/chat"
              element={
                <ProtectedRoute>
                  <ChatPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/chat/:username"
              element={
                <ProtectedRoute>
                  <ChatPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/support"
              element={
                <ProtectedRoute>
                  <SupportChatPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/payment"
              element={
                <AdminProtectedRoute>
                  <PaymentPage />
                </AdminProtectedRoute>
              }
            />
            <Route
              path="/classes"
              element={
                <AdminProtectedRoute>
                  <ClassesPage />
                </AdminProtectedRoute>
              }
            />
            <Route
              path="/calendar"
              element={
                <ProtectedRoute>
                  <CalendarPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/members"
              element={
                <ProtectedRoute>
                  <MembersPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/leaderboard"
              element={
                <ProtectedRoute>
                  <LeaderboardPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <ProfilePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <SettingsPage />
                </ProtectedRoute>
              }
            >
              <Route index element={<Navigate to="/settings/community" replace />} />
              <Route path=":section" element={<SettingsPage />} />
            </Route>
            <Route
              path="/tasks"
              element={
                <ProtectedRoute>
                  <TasksPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/free-keys"
              element={
                <ProtectedRoute>
                  <FreeKeysPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/transcript"
              element={
                <ProtectedRoute>
                  <TranscriptPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/check-view"
              element={
                <ProtectedRoute>
                  <CheckViewPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/event"
              element={
                <ProtectedRoute>
                  <EventPage />
                </ProtectedRoute>
              }
            />
            {/* User profile routes */}
            <Route
              path="/u/:username"
              element={
                <ProtectedRoute>
                  <UserProfilePage />
                </ProtectedRoute>
              }
            />

            {/* Admin routes */}
            <Route
              path="/admin"
              element={
                <ProtectedRoute>
                  <AdminLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Navigate to="/admin/users" replace />} />
              <Route path="users" element={<AdminUsersPage />} />
              <Route path="fake-users" element={<AdminFakeUsersPage />} />
              <Route path="videos" element={<AdminUploadVideoPage />} />
              <Route path="enrollments" element={<AdminEnrollmentsPage />} />
              <Route path="categories" element={<AdminCategoriesPage />} />
              <Route path="post-categories" element={<AdminPostCategoriesPage />} />
              <Route path="courses" element={<AdminCoursesPage />} />
              <Route path="courses/:courseId" element={<AdminCourseDetailPage />} />
              <Route path="posts" element={<AdminPostsPage />} />
              <Route path="tasks" element={<AdminTasksPage />} />
              <Route path="course-tasks" element={<AdminCourseTasksPage />} />
              <Route path="levels" element={<AdminLevelsPage />} />
              <Route path="school-slides" element={<AdminSchoolSlidesPage />} />
              <Route path="youtube-notifications" element={<AdminYouTubeNotificationsPage />} />
              <Route path="youtube-view" element={<AdminYouTubeViewPage />} />
              <Route path="free-keys" element={<AdminFreeKeysPage />} />
              <Route path="transcript-tokens" element={<AdminTranscriptTokensPage />} />
              <Route path="events" element={<AdminEventsPage />} />
            </Route>

            {/* Redirect tất cả các route không hợp lệ */}
            <Route
              path="*"
              element={isAuthenticated ? <Navigate to="/" replace /> : <Navigate to="/about" replace />}
            />
          </Routes>
        </main>
        {/* Show Footer on authenticated pages except landing page (it has its own LandingFooter) */}
        {showNavbar && !isLandingRoute && <Footer />}
      </div>
    </ToastProvider>
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </Router>
  );
}

export default App;


